import React, { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';

type DemoAccount = {
  demoKey: string;
  label: string;
  createdAt?: Timestamp | null;
  firstAccessAt?: Timestamp | null;
  lastAccessAt?: Timestamp | null;
  accessCount?: number;
};

function demoUrl(demoKey: string): string {
  return `${window.location.origin}/#demo=${demoKey}`;
}

function fmt(ts?: Timestamp | null): string {
  if (!ts?.toDate) return '—';
  return ts.toDate().toLocaleString();
}

/**
 * Dev-only sales dashboard: create pre-branded demo admin accounts (magic links
 * to paste into outreach) and monitor who has logged in. Login tracking
 * (first/last seen, visit count) is the warm-lead signal.
 */
export const DemoAccounts: React.FC = () => {
  const [accounts, setAccounts] = useState<DemoAccount[]>([]);
  const [label, setLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justCreated, setJustCreated] = useState<{ label: string; url: string } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'demoAccounts'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => setAccounts(snap.docs.map((d) => d.data() as DemoAccount)),
      (err) => setError(err.message),
    );
    return () => unsub();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = label.trim();
    if (!name) return;
    setCreating(true);
    setError(null);
    try {
      const fn = httpsCallable<{ label: string }, { url: string; label: string; demoKey: string }>(
        functions,
        'createDemoAdmin',
      );
      const { data } = await fn({ label: name });
      // Prefer the current origin so the link matches where you're working.
      setJustCreated({ label: data.label, url: demoUrl(data.demoKey) });
      setLabel('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create demo account.');
    } finally {
      setCreating(false);
    }
  };

  const copy = (url: string) => {
    navigator.clipboard?.writeText(url).then(() => {
      setCopied(url);
      setTimeout(() => setCopied((c) => (c === url ? null : c)), 1500);
    });
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-white">Demo links</h3>
        <p className="text-sm text-gray-400">
          Create a pre-branded admin preview for a prospect, paste the magic link into your outreach,
          and watch below to see when they log in.
        </p>
      </div>

      <form onSubmit={handleCreate} className="flex gap-2">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Prospect name (e.g. Steven Bridges)"
          className="flex-1 px-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-white text-sm"
        />
        <button
          type="submit"
          disabled={creating || !label.trim()}
          className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold disabled:opacity-60"
        >
          {creating ? 'Creating…' : 'Create link'}
        </button>
      </form>

      {justCreated && (
        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-3">
          <div className="text-sm text-white font-semibold mb-1">Link for {justCreated.label}</div>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={justCreated.url}
              className="flex-1 px-2 py-1.5 rounded bg-gray-900 border border-gray-700 text-emerald-300 text-xs"
              onFocus={(e) => e.currentTarget.select()}
            />
            <button
              onClick={() => copy(justCreated.url)}
              className="px-3 py-1.5 rounded bg-gray-700 hover:bg-gray-600 text-white text-xs font-semibold"
            >
              {copied === justCreated.url ? 'Copied ✓' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      {error && <div className="text-sm text-red-400">{error}</div>}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-700">
              <th className="py-2 pr-3 font-medium">Prospect</th>
              <th className="py-2 pr-3 font-medium">First seen</th>
              <th className="py-2 pr-3 font-medium">Last seen</th>
              <th className="py-2 pr-3 font-medium">Visits</th>
              <th className="py-2 font-medium">Link</th>
            </tr>
          </thead>
          <tbody>
            {accounts.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-4 text-gray-500">No demo links yet.</td>
              </tr>
            ) : (
              accounts.map((a) => {
                const opened = (a.accessCount ?? 0) > 0;
                return (
                  <tr key={a.demoKey} className="border-b border-gray-800">
                    <td className="py-2 pr-3 text-white">
                      <span
                        className={`inline-block w-2 h-2 rounded-full mr-2 ${opened ? 'bg-emerald-400' : 'bg-gray-600'}`}
                        title={opened ? 'Opened' : 'Not opened yet'}
                      />
                      {a.label}
                    </td>
                    <td className="py-2 pr-3 text-gray-300">{fmt(a.firstAccessAt)}</td>
                    <td className="py-2 pr-3 text-gray-300">{fmt(a.lastAccessAt)}</td>
                    <td className="py-2 pr-3 text-gray-300">{a.accessCount ?? 0}</td>
                    <td className="py-2">
                      <button
                        onClick={() => copy(demoUrl(a.demoKey))}
                        className="px-2.5 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white text-xs font-semibold"
                      >
                        {copied === demoUrl(a.demoKey) ? 'Copied ✓' : 'Copy link'}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
